import React from 'react';

import { useEnableThemeAppExtension } from '~/modules/apps/hooks/useEnableThemeAppExtension';
import { useGModalConfirm, GModalConfirm, GModalConfirmType } from '~/presentation/common';
import { BlockStack, Box, Grid, Scrollable, Text } from '@shopify/polaris';

import THEME_GEMX_STEP_1 from '~/modules/apps/assets/theme-extension/step-1.png';
import THEME_GEMX_STEP_2 from '~/modules/apps/assets/theme-extension/step-2.png';

const StepCard = React.memo(
  ({ imgSrc, step, description }: { imgSrc: string; step: string; description: string }) => (
    <Grid.Cell columnSpan={{ xs: 6 }}>
      <Box borderRadius={'300'} borderColor={'border-tertiary'} borderWidth={'025'}>
        <BlockStack>
          <Box borderRadius={'300'} overflowX={'hidden'} overflowY={'hidden'}>
            <div className={'flex'} style={{ height: '84px' }}>
              <img
                style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                src={imgSrc}
                alt={step}
                loading="lazy"
              />
            </div>
          </Box>
        </BlockStack>
        <Box borderColor={'border-tertiary'} borderBlockStartWidth={'025'} padding={'400'}>
          <BlockStack gap={'200'}>
            <Text as="p" variant="bodyMd" fontWeight={'semibold'}>
              {step}
            </Text>
            <Text as="p" variant="bodyMd" tone={'subdued'}>
              {description}
            </Text>
          </BlockStack>
        </Box>
      </Box>
    </Grid.Cell>
  ),
);

export const ModalThemeExtension = () => {
  const { isLoadingButton, isShowBanner, setIsLoadingButton, onHandleEnableThemeAppExtension } =
    useEnableThemeAppExtension();
  const { onCloseModal } = useGModalConfirm();

  const handleCloseModal = () => {
    setIsLoadingButton(false);
    onCloseModal();
  };

  if (!isShowBanner) return null;

  return (
    <GModalConfirm
      type={GModalConfirmType.THEME_EXTENSION}
      title="Enable GemX in your theme before starting a campaign"
      size="fullScreen"
      primaryAction={{
        content: 'Go to Shopify Editor',
        onAction: onHandleEnableThemeAppExtension,
        loading: isLoadingButton,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleCloseModal,
        },
      ]}
    >
      <Scrollable
        vertical
        scrollbarWidth="thin"
        horizontal={false}
        className={'md:max-h-[50vh] max-h-[calc(100vh-57px-65px-32px)] max-w-[980px]'}
      >
        <BlockStack gap={'400'}>
          <Box
            borderRadius={'300'}
            borderColor={'border-tertiary'}
            borderWidth={'025'}
            padding={'400'}
          >
            <BlockStack gap={'200'}>
              <Text as="h4" variant="headingMd">
                Why enable this
              </Text>
              <Text as="p" variant="bodyMd">
                To run GemX seamlessly, ensure the GemX Theme Helper is enabled in your theme. This
                is essential for GemX functions to work properly with campaigns and to support the
                preview feature. This action doesn't affect any changes in your store. Learn more.
              </Text>
            </BlockStack>
          </Box>

          <Box
            borderRadius={'300'}
            borderColor={'border-tertiary'}
            borderWidth={'025'}
            padding={'400'}
          >
            <BlockStack gap={'400'}>
              <Text as="h4" variant="headingMd">
                Setup guide
              </Text>
              <Grid gap={{ xs: '200' }}>
                <StepCard
                  imgSrc={THEME_GEMX_STEP_1}
                  step="1. Turn on GemX Theme Helper"
                  description="From Shopify's left side bar"
                />
                <StepCard
                  imgSrc={THEME_GEMX_STEP_2}
                  step="2. Click 'Save' button"
                  description="From Shopify's top right corner"
                />
              </Grid>
            </BlockStack>
          </Box>
        </BlockStack>
      </Scrollable>
    </GModalConfirm>
  );
};
